import { cn } from '@/utils/cn';

import { Button } from './Button';
import { Icon } from './Icon';

// --------------------------------------------------------------------------- //
// Loading
// --------------------------------------------------------------------------- //
export function Spinner({ className }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn(
        'border-brand-500 inline-block size-5 animate-spin rounded-full border-2 border-t-transparent',
        className,
      )}
    />
  );
}

export function LoadingState({ label = 'Loading…' }) {
  return (
    <div className="text-ink-muted flex flex-col items-center gap-3 py-12">
      <Spinner />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function Skeleton({ className }) {
  return (
    <div
      className={cn('bg-surface-muted animate-pulse rounded-md', className)}
      aria-hidden="true"
    />
  );
}

// --------------------------------------------------------------------------- //
// Empty / error
// --------------------------------------------------------------------------- //

/**
 * "Nothing here", said the same way everywhere.
 *
 * `compact` is for a section inside a card or a detail page ("No invoices
 * yet") -- a quiet one-liner with a small mark, where the full-size version
 * would outweigh the card around it.
 */
export function EmptyState({ title, description, action, icon, compact = false }) {
  if (compact) {
    return (
      <div className="bg-surface-muted/60 rounded-input flex items-start gap-3 px-4 py-3">
        <span className="text-ink-muted mt-0.5 shrink-0">
          {icon ?? <Icon name="document" size={16} />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-ink text-sm font-medium">{title}</p>
          {description && <p className="text-ink-muted mt-0.5 text-sm">{description}</p>}
          {action && <div className="mt-2">{action}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="border-brand-200/70 bg-brand-50/40 rounded-card flex flex-col items-center gap-3 border border-dashed px-6 py-12 text-center">
      {/* Without an icon of its own, a soft mark still gives the eye somewhere
          to land -- a bare line of text in a dashed box reads as broken. */}
      <div className="bg-surface text-brand-500 shadow-card grid size-12 place-items-center rounded-full text-2xl">
        {icon ?? <Icon name="document" size={22} />}
      </div>
      <h3 className="text-ink font-semibold">{title}</h3>
      {description && <p className="text-ink-muted max-w-md text-sm">{description}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ title = 'Something went wrong', message, onRetry }) {
  return (
    <div
      role="alert"
      className="rounded-card border-danger-200 bg-danger-50 flex flex-col items-center border px-6 py-8 text-center"
    >
      <span className="bg-surface text-danger-600 shadow-card mb-3 grid size-11 place-items-center rounded-full">
        <Icon name="alert" size={20} />
      </span>
      <h3 className="text-danger-800 font-semibold">{title}</h3>
      {message && <p className="text-danger-700 mt-1 max-w-md text-sm">{message}</p>}
      {onRetry && (
        <Button variant="secondary" size="sm" className="mt-4" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Inline messages
// --------------------------------------------------------------------------- //

const TONES = {
  info: 'border-brand-200 bg-brand-50 text-brand-800',
  success: 'border-success-200 bg-success-50 text-success-800',
  warning: 'border-warning-200 bg-warning-50 text-warning-800',
  error: 'border-danger-200 bg-danger-50 text-danger-800',
};

/** An icon per tone, so the message does not rely on colour alone. */
const TONE_ICONS = {
  info: 'info',
  success: 'check',
  warning: 'alert',
  error: 'alert',
};

export function Alert({ tone = 'info', title, children, className }) {
  const known = TONES[tone] ? tone : 'info';
  return (
    <div
      role={known === 'error' ? 'alert' : 'status'}
      className={cn('rounded-input flex gap-3 border px-4 py-3 text-sm', TONES[known], className)}
    >
      <Icon name={TONE_ICONS[known]} size={18} className="mt-px opacity-80" />
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(title && 'mt-1')}>{children}</div>}
      </div>
    </div>
  );
}
